import { useAuthController, useDataSource, useSnackbarController } from "@firecms/core";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LoadingButton,
  TextField,
  Typography,
} from "@firecms/ui";
import { useState } from "react";

// The lifecycle write lives in `apple_delegated_onboarding.write.ts` — the console's single
// Firestore write boundary — not in a file named after this surface. See that file's header.
import { queueShowcaseLifecycleAction } from "./apple_delegated_onboarding.write";
import {
  SHOWCASE_LIFECYCLE_ACTION_LABELS,
  ShowcaseLifecycleActionEnum,
} from "./apple_showcase_lifecycle.types";
import {
  describeFlightWindow,
  evaluateLifecycleGate,
  ShowcaseSubject,
} from "./apple_showcase_lifecycle.utils";

// 🛑 THE TWO-STEP CONFIRMATION FOR A SHOWCASE LIFECYCLE ACTION.
//
// Guard rail 2 of this ticket, and the only thing standing between a stray click and a real
// business's place-card promotion. Two steps, always, for DEACTIVATE and DELETE:
//
//   Step 1 — REVIEW. Names the showcase by id, names its flight window, and names the LAST VISIBLE
//            DAY (the end date is exclusive, so the raw value misleads by a day). States what the
//            action does and, plainly, that it cannot be undone.
//   Step 2 — TYPE TO CONFIRM. The operator types the showcase id. Not decoration: it is the only
//            mechanism that makes acting on the WRONG showcase require a deliberate mistake rather
//            than a mis-click, and this console has several near-identical showcase rows per client.
//
// EXTEND gets one step plus a date field. It is additive — the worst case is a longer run of a
// promotion the client already agreed to — so a typed confirmation there would be ceremony that
// teaches operators to type without reading, which is exactly what would make step 2 useless where it
// matters.
//
// ⚠️ NEVER SAY OR IMPLY THAT A SHOWCASE CAN BE RESTORED. Apple ships an `undelete` for LOCATION
// assets. A showcase has neither an undelete nor a re-activate: deactivating stops it displaying
// forever, and running the promotion again means a new showcase, a new creative and a new Apple
// review. Deleting does all of that AND removes the history. If you are editing this copy, that
// distinction is the point of it.

/**
 * Two steps for a destructive action, one for an extension. `review` is always the entry point — there
 * is no path that opens straight onto the confirm button.
 */
type Step = { kind: "review" } | { kind: "confirm" };

const isDestructive = (action: ShowcaseLifecycleActionEnum): boolean =>
  action !== ShowcaseLifecycleActionEnum.EXTEND;

export function ShowcaseLifecycleDialog({
  open,
  onOpenChange,
  action,
  subject,
  showcaseRequestPath,
  showcaseRequestId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  action: ShowcaseLifecycleActionEnum;
  subject: ShowcaseSubject;
  showcaseRequestPath: string;
  showcaseRequestId: string;
}) {
  const dataSource = useDataSource();
  const snackbarController = useSnackbarController();
  const authController = useAuthController();

  const [step, setStep] = useState<Step>({ kind: "review" });
  const [typedId, setTypedId] = useState("");
  const [requestedEndDate, setRequestedEndDate] = useState("");
  const [deleteCreative, setDeleteCreative] = useState(false);
  const [saving, setSaving] = useState(false);

  const close = () => {
    onOpenChange(false);
    // Reset only after closing, so the dialog does not visibly flicker back to step 1.
    setStep({ kind: "review" });
    setTypedId("");
    setRequestedEndDate("");
    setDeleteCreative(false);
  };

  /**
   * ⚠️ EVERY CLOSE PATH MUST GO THROUGH `close()`, INCLUDING THE BACKDROP AND ESCAPE.
   *
   * This used to pass `onOpenChange` straight to `Dialog`, which meant a backdrop click or an Escape
   * press dismissed the dialog WITHOUT resetting `step` or `typedId` — so reopening it landed on the
   * confirmation step with the typed id still matching, one click from a delete, having skipped the
   * review step that names the showcase and its dates. That is the two-step confirmation this whole
   * surface exists to provide, defeated by the most ordinary way of closing a dialog.
   *
   * Same shape as `ConnectLinkDialog`; kept as a named handler rather than an inline arrow so the
   * reason survives next to it.
   */
  const handleOpenChange = (next: boolean) => (next ? onOpenChange(true) : close());

  const gate = evaluateLifecycleGate({ action, subject, requestedEndDate });
  const destructive = isDestructive(action);
  const idMatches = typedId.trim() === (subject.showcaseId ?? "");

  const submit = async () => {
    setSaving(true);
    try {
      const ok = await queueShowcaseLifecycleAction(
        { dataSource, snackbarController },
        {
          showcaseRequestPath,
          showcaseRequestId,
          action,
          subject,
          requestedEndDate: requestedEndDate || undefined,
          deleteCreative,
          requestedBy: authController.user?.email ?? undefined,
        },
      );
      // Only close on success. A failed write leaves the dialog where it was, with the snackbar
      // explaining why, rather than dismissing itself and looking like it worked.
      if (ok) close();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} maxWidth="xl">
      <DialogTitle variant="h6">{SHOWCASE_LIFECYCLE_ACTION_LABELS[action]}</DialogTitle>

      <DialogContent className="flex flex-col gap-4">
        {!gate.allowed && <Alert color="warning">{gate.reason}</Alert>}

        {gate.allowed && (
          <>
            <SubjectSummary subject={subject} />
            <ConsequenceNotice action={action} />

            {action === ShowcaseLifecycleActionEnum.EXTEND && (
              <TextField
                label="New end date (EXCLUSIVE)"
                value={requestedEndDate}
                onChange={(event) => setRequestedEndDate(event.target.value)}
                placeholder="2026-03-31T00:00:00Z"
              />
            )}

            {action === ShowcaseLifecycleActionEnum.DELETE && (
              <CreativeChoice
                value={deleteCreative}
                onChange={setDeleteCreative}
                creativeId={subject.creativeId}
              />
            )}

            {gate.cautions.map((caution) => (
              <Alert key={caution} color="info">
                {caution}
              </Alert>
            ))}

            {destructive && step.kind === "confirm" && (
              <>
                <Alert color="error">
                  Last check. Type the showcase id <strong>{subject.showcaseId}</strong> below to
                  confirm you are acting on this showcase and not another one on this client.
                </Alert>
                <TextField
                  label="Showcase id"
                  value={typedId}
                  onChange={(event) => setTypedId(event.target.value)}
                  placeholder={subject.showcaseId}
                />
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="text" onClick={close}>
          Cancel
        </Button>

        {gate.allowed && destructive && step.kind !== "confirm" && (
          // Step 1 -> step 2. A separate press, on purpose: the review has to be read before the
          // confirmation exists to be clicked.
          <Button color="error" onClick={() => setStep({ kind: "confirm" })}>
            Continue
          </Button>
        )}

        {gate.allowed && destructive && step.kind === "confirm" && (
          <LoadingButton
            color="error"
            variant="filled"
            loading={saving}
            disabled={!idMatches}
            onClick={() => void submit()}
          >
            {action === ShowcaseLifecycleActionEnum.DELETE
              ? "Delete permanently"
              : "Deactivate permanently"}
          </LoadingButton>
        )}

        {gate.allowed && !destructive && (
          <LoadingButton
            color="primary"
            variant="filled"
            loading={saving}
            onClick={() => void submit()}
          >
            Queue extension
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Names the showcase and its dates. Guard rail 2 requires both, in the confirmation itself. */
function SubjectSummary({ subject }: { subject: ShowcaseSubject }) {
  return (
    <div className="flex flex-col gap-1">
      <Typography variant="body2">
        Apple showcase <strong>{subject.showcaseId}</strong>
        {subject.locationId ? ` on location ${subject.locationId}` : ""}.
      </Typography>
      <Typography variant="body2">{describeFlightWindow(subject)}</Typography>
      <Typography variant="caption">
        These dates are what DearDoc submitted. The backend reads the showcase back from Apple
        before it acts, and Apple&apos;s own record is what decides whether the action is permitted.
      </Typography>
    </div>
  );
}

/**
 * The consequence, per action, in the operator's language.
 *
 * ⚠️ THE DEACTIVATE AND DELETE TEXTS ARE DIFFERENT ON PURPOSE and must stay that way. Both are
 * permanent; only one removes the history. Collapsing them into one "this is permanent" warning is how
 * an operator ends up deleting when deactivating would have done.
 */
function ConsequenceNotice({ action }: { action: ShowcaseLifecycleActionEnum }) {
  if (action === ShowcaseLifecycleActionEnum.DEACTIVATE) {
    return (
      <Alert color="error">
        This stops the showcase displaying on the client&apos;s Apple place card.{" "}
        <strong>It cannot be undone and it cannot be switched back on.</strong> Running this
        promotion again means creating a new showcase — a new image creative, a new Apple review of
        it, and a new set of dates. Apple&apos;s &ldquo;undelete&rdquo; applies to location assets,
        not to showcases.
      </Alert>
    );
  }
  if (action === ShowcaseLifecycleActionEnum.DELETE) {
    return (
      <Alert color="error">
        This removes the showcase <strong>and its history</strong> from Apple.{" "}
        <strong>It cannot be undone — there is no undelete for a showcase.</strong> If the aim is
        only to stop it appearing, close this and <strong>deactivate it instead</strong>: that stops
        the display and keeps the record of what ran. Apple also refuses to delete a showcase that
        is currently live, so the backend will refuse this unless it can prove the showcase is not
        live.
      </Alert>
    );
  }
  return (
    <Alert color="info">
      This asks Apple to let the showcase run longer. Apple measures the limit from the
      showcase&apos;s <strong>original start date</strong> — not from today and not from its current
      end date — so a short showcase cannot be extended indefinitely by repeating this. Nothing else
      about the showcase changes.
    </Alert>
  );
}

/**
 * The creative choice on a delete.
 *
 * ⚠️ DEFAULTS TO OFF, AND THE COPY SAYS WHY. Removing both resources is a two-call sequence; the
 * backend checkpoints between them precisely because a failure in the middle leaves an unreferenced
 * creative. An unreferenced creative is harmless — it is not visible anywhere on its own — so the
 * default is to leave it.
 */
function CreativeChoice({
  value,
  onChange,
  creativeId,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  creativeId?: string;
}) {
  if (!creativeId) return null;
  return (
    <div className="flex flex-col gap-1">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={value}
          onChange={(event) => onChange(event.target.checked)}
        />
        <Typography variant="body2">
          Also delete the image creative {creativeId} (optional)
        </Typography>
      </label>
      <Typography variant="caption">
        Leave this off unless you specifically want the creative gone. The showcase is deleted first
        and the result recorded before the creative is touched, so if the second step fails you will
        see exactly which resource is left behind. A creative nothing references is not visible to
        anyone.
      </Typography>
    </div>
  );
}
